import React, { useState, useEffect } from 'react';
import '../../styles/ReportBuilder.css';

const ReportBuilder = ({ onGenerateReport, candidates, actionReports, districts, loading }) => {
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    dataSource: 'candidates',
    columns: [],
    filters: [],
    sorting: { field: '', direction: 'asc' },
    groupBy: '',
    aggregations: [],
    dateRange: { start: '', end: '' }
  });

  const [availableFields, setAvailableFields] = useState([]);
  const [presetTemplates, setPresetTemplates] = useState([]);

  useEffect(() => {
    updateAvailableFields();
    initializePresetTemplates();
  }, [reportConfig.dataSource, candidates, actionReports]);

  const updateAvailableFields = () => {
    let fields = [];
    
    switch (reportConfig.dataSource) {
      case 'candidates':
        fields = [
          { key: 'firstName', label: 'First Name', type: 'text' },
          { key: 'lastName', label: 'Last Name', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
          { key: 'conferenceStatus', label: 'Conference Status', type: 'select' },
          { key: 'processStage', label: 'Process Stage', type: 'select' },
          { key: 'district', label: 'District', type: 'select' },
          { key: 'homeChurch', label: 'Home Church', type: 'text' },
          { key: 'currentAppointment', label: 'Current Appointment', type: 'text' },
          { key: 'mentor', label: 'Mentor', type: 'text' },
          { key: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
          { key: 'gender', label: 'Gender', type: 'select' },
          { key: 'ethnicity', label: 'Ethnicity', type: 'select' },
          { key: 'language', label: 'Primary Language', type: 'select' },
          { key: 'createdAt', label: 'Created Date', type: 'date' },
          { key: 'updatedAt', label: 'Last Updated', type: 'date' }
        ];
        break;
        
      case 'actionReports':
        fields = [
          { key: 'candidateId', label: 'Candidate ID', type: 'text' },
          { key: 'category', label: 'Category', type: 'select' },
          { key: 'type', label: 'Report Type', type: 'select' },
          { key: 'status', label: 'Status', type: 'select' },
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'text' },
          { key: 'recommendation', label: 'Recommendation', type: 'text' },
          { key: 'vote', label: 'Vote', type: 'select' },
          { key: 'submittedBy', label: 'Submitted By', type: 'text' },
          { key: 'submittedAt', label: 'Submitted Date', type: 'date' },
          { key: 'reviewedBy', label: 'Reviewed By', type: 'text' },
          { key: 'reviewedAt', label: 'Reviewed Date', type: 'date' },
          { key: 'createdAt', label: 'Created Date', type: 'date' }
        ];
        break;
        
      case 'courseOfStudy':
        fields = [
          { key: 'firstName', label: 'First Name', type: 'text' },
          { key: 'lastName', label: 'Last Name', type: 'text' },
          { key: 'courseOfStudy.completedCredits', label: 'Completed Credits', type: 'number' },
          { key: 'courseOfStudy.totalCredits', label: 'Total Credits', type: 'number' },
          { key: 'courseOfStudy.completionPercentage', label: 'Completion %', type: 'number' },
          { key: 'courseOfStudy.courses', label: 'Courses', type: 'array' },
          { key: 'district', label: 'District', type: 'select' },
          { key: 'conferenceStatus', label: 'Conference Status', type: 'select' }
        ];
        break;
        
      case 'combined':
        fields = [
          { key: 'firstName', label: 'First Name', type: 'text' },
          { key: 'lastName', label: 'Last Name', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
          { key: 'conferenceStatus', label: 'Conference Status', type: 'select' },
          { key: 'processStage', label: 'Process Stage', type: 'select' },
          { key: 'district', label: 'District', type: 'select' },
          { key: 'totalReports', label: 'Total Reports', type: 'number' },
          { key: 'latestReport.category', label: 'Latest Report Category', type: 'select' },
          { key: 'latestReport.status', label: 'Latest Report Status', type: 'select' },
          { key: 'latestReport.vote', label: 'Latest Vote', type: 'select' },
          { key: 'latestReport.submittedAt', label: 'Latest Report Date', type: 'date' }
        ];
        break;
        
      default:
        fields = [];
    }
    
    setAvailableFields(fields);
  };

  const initializePresetTemplates = () => {
    const templates = [
      {
        name: 'Candidate Summary',
        description: 'Basic candidate information and status',
        config: {
          dataSource: 'candidates',
          columns: ['firstName', 'lastName', 'email', 'conferenceStatus', 'processStage', 'district'],
          filters: [],
          sorting: { field: 'lastName', direction: 'asc' }
        }
      },
      {
        name: 'Action Reports by Category',
        description: 'All action reports grouped by category',
        config: {
          dataSource: 'actionReports',
          columns: ['category', 'type', 'status', 'vote', 'submittedAt'],
          filters: [],
          groupBy: 'category',
          sorting: { field: 'submittedAt', direction: 'desc' }
        }
      },
      {
        name: 'Course of Study Progress',
        description: 'Course completion status for all candidates',
        config: {
          dataSource: 'courseOfStudy',
          columns: ['firstName', 'lastName', 'courseOfStudy.completedCredits', 'courseOfStudy.totalCredits', 'courseOfStudy.completionPercentage'],
          filters: [],
          sorting: { field: 'courseOfStudy.completionPercentage', direction: 'desc' }
        }
      },
      {
        name: 'Voting Summary',
        description: 'Summary of votes by candidate',
        config: {
          dataSource: 'combined',
          columns: ['firstName', 'lastName', 'totalReports', 'latestReport.vote', 'latestReport.submittedAt'],
          filters: [{ field: 'latestReport.vote', operator: 'notEmpty', value: '' }],
          sorting: { field: 'latestReport.submittedAt', direction: 'desc' }
        }
      },
      {
        name: 'District Summary',
        description: 'Candidates grouped by district',
        config: {
          dataSource: 'candidates',
          columns: ['firstName', 'lastName', 'conferenceStatus', 'processStage'],
          filters: [],
          groupBy: 'district',
          sorting: { field: 'lastName', direction: 'asc' }
        }
      }
    ];
    
    setPresetTemplates(templates);
  };

  const handleFieldChange = (field, value) => {
    setReportConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnToggle = (fieldKey) => {
    setReportConfig(prev => ({
      ...prev,
      columns: prev.columns.includes(fieldKey)
        ? prev.columns.filter(col => col !== fieldKey)
        : [...prev.columns, fieldKey]
    }));
  };

  const handleAddFilter = () => {
    setReportConfig(prev => ({
      ...prev,
      filters: [...prev.filters, { field: '', operator: 'equals', value: '' }]
    }));
  };

  const handleUpdateFilter = (index, field, value) => {
    setReportConfig(prev => ({
      ...prev,
      filters: prev.filters.map((filter, i) => 
        i === index ? { ...filter, [field]: value } : filter
      )
    }));
  };

  const handleRemoveFilter = (index) => {
    setReportConfig(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const handleLoadTemplate = (template) => {
    setReportConfig(prev => ({
      ...prev,
      ...template.config,
      name: template.name,
      description: template.description
    }));
  };

  const handleGenerateReport = () => {
    if (reportConfig.columns.length === 0) {
      alert('Please select at least one column to include in the report.');
      return;
    }
    
    onGenerateReport(reportConfig);
  };

  const getFieldLabel = (fieldKey) => {
    const field = availableFields.find(f => f.key === fieldKey);
    return field ? field.label : fieldKey;
  };

  const getFilterOperators = (fieldType) => {
    const baseOperators = [
      { value: 'equals', label: 'Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'notEmpty', label: 'Not Empty' },
      { value: 'isEmpty', label: 'Is Empty' }
    ];

    if (fieldType === 'number' || fieldType === 'date') {
      return [
        ...baseOperators,
        { value: 'greaterThan', label: 'Greater Than' },
        { value: 'lessThan', label: 'Less Than' },
        { value: 'between', label: 'Between' }
      ];
    }

    if (fieldType === 'text') {
      return [
        ...baseOperators,
        { value: 'startsWith', label: 'Starts With' }
      ];
    }

    if (fieldType === 'select') {
      return [
        ...baseOperators,
        { value: 'in', label: 'In List' }
      ];
    }

    return baseOperators;
  };

  return (
    <div className="report-builder">
      <div className="builder-section">
        <h2>Report Configuration</h2>
        
        {/* Basic Info */}
        <div className="config-group">
          <h3>Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Report Name</label>
              <input
                type="text"
                value={reportConfig.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Enter report name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                value={reportConfig.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Enter report description"
              />
            </div>
          </div>
        </div>

        {/* Preset Templates */}
        <div className="config-group">
          <h3>Quick Start Templates</h3>
          <div className="template-grid">
            {presetTemplates.map((template, index) => (
              <div key={index} className="template-card">
                <h4>{template.name}</h4>
                <p>{template.description}</p>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleLoadTemplate(template)}
                >
                  Load Template
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Data Source */}
        <div className="config-group">
          <h3>Data Source</h3>
          <select
            value={reportConfig.dataSource}
            onChange={(e) => handleFieldChange('dataSource', e.target.value)}
            className="form-select"
          >
            <option value="candidates">Candidates</option>
            <option value="actionReports">Action Reports</option>
            <option value="courseOfStudy">Course of Study</option>
            <option value="combined">Combined Data</option>
          </select>
        </div>

        {/* Columns Selection */}
        <div className="config-group">
          <h3>Columns to Include</h3>
          <div className="columns-grid">
            {availableFields.map(field => (
              <label key={field.key} className="column-checkbox">
                <input
                  type="checkbox"
                  checked={reportConfig.columns.includes(field.key)}
                  onChange={() => handleColumnToggle(field.key)}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="config-group">
          <h3>Filters</h3>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={handleAddFilter}
          >
            Add Filter
          </button>
          
          {reportConfig.filters.map((filter, index) => (
            <div key={index} className="filter-row">
              <select
                value={filter.field}
                onChange={(e) => handleUpdateFilter(index, 'field', e.target.value)}
                className="form-select"
              >
                <option value="">Select Field</option>
                {availableFields.map(field => (
                  <option key={field.key} value={field.key}>{field.label}</option>
                ))}
              </select>
              
              <select
                value={filter.operator}
                onChange={(e) => handleUpdateFilter(index, 'operator', e.target.value)}
                className="form-select"
              >
                {getFilterOperators(availableFields.find(f => f.key === filter.field)?.type).map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              
              <input
                type="text"
                value={filter.value}
                onChange={(e) => handleUpdateFilter(index, 'value', e.target.value)}
                placeholder="Filter value"
                className="form-input"
              />
              
              <button 
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveFilter(index)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {/* Sorting */}
        <div className="config-group">
          <h3>Sorting</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Sort By</label>
              <select
                value={reportConfig.sorting.field}
                onChange={(e) => handleFieldChange('sorting', { ...reportConfig.sorting, field: e.target.value })}
                className="form-select"
              >
                <option value="">No Sorting</option>
                {availableFields.map(field => (
                  <option key={field.key} value={field.key}>{field.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Direction</label>
              <select
                value={reportConfig.sorting.direction}
                onChange={(e) => handleFieldChange('sorting', { ...reportConfig.sorting, direction: e.target.value })}
                className="form-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grouping */}
        <div className="config-group">
          <h3>Grouping</h3>
          <select
            value={reportConfig.groupBy}
            onChange={(e) => handleFieldChange('groupBy', e.target.value)}
            className="form-select"
          >
            <option value="">No Grouping</option>
            {availableFields.map(field => (
              <option key={field.key} value={field.key}>{field.label}</option>
            ))}
          </select>
        </div>

        {/* Generate Button */}
        <div className="config-group">
          <button 
            className="btn btn-primary btn-lg"
            onClick={handleGenerateReport}
            disabled={loading || reportConfig.columns.length === 0}
          >
            {loading ? 'Generating Report...' : 'Generate Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
